import React, { useState } from 'react';
import CMSCustomSections from '../components/CMSCustomSections';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Handshake, 
  Heart, 
  Building2, 
  Car, 
  CheckCircle,
  ArrowRight,
  DollarSign,
  Users,
  Sparkles,
  FileText
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { createPartnerApplication } from '../api';
import { useCMS, getImageUrl } from '../hooks/useCMS';

const Partners = () => {
  const navigate = useNavigate();
  const { content } = useCMS('partners');
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    partnerType: '',
    website: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedApplication, setSubmittedApplication] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await createPartnerApplication(formData);
      setSubmittedApplication(response);
      toast({
        title: "Partnership Application Received!",
        description: `Reference: ${response.applicationId}. Our partnerships team will contact you within 48 hours.`
      });
      setFormData({ businessName: '', contactName: '', email: '', phone: '', partnerType: '', website: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const partnerTypes = [
    {
      icon: Heart,
      title: 'Wedding Planners & Venues',
      description: 'Provide premium send-off experiences for your clients',
      benefits: [
        'Referral commissions on all bookings',
        'Co-branded marketing materials',
        'Direct booking line for your clients',
        'Priority availability for your events',
        'White-glove coordination support'
      ]
    },
    {
      icon: Building2,
      title: 'Hotels & Hospitality',
      description: 'Elevate guest experience with luxury ground transportation',
      benefits: [
        'Guest transportation programs',
        'Concierge partnership integration',
        'Commission structure options',
        'Branded guest experience',
        'VIP guest handling'
      ]
    },
    {
      icon: Car,
      title: 'Dealerships & Auto Services',
      description: 'Expand your service offerings with our concierge solutions',
      benefits: [
        'Customer referral programs',
        'Fleet management partnerships',
        'Courtesy transportation services',
        'Service loaner coordination',
        'Customer delivery services'
      ]
    },
    {
      icon: Users,
      title: 'Corporate & Event Planners',
      description: 'Seamless transportation for conferences and corporate events',
      benefits: [
        'Volume-based pricing',
        'Dedicated event coordinator',
        'Multi-vehicle dispatch capability',
        'Real-time tracking for organizers',
        'Custom reporting and invoicing'
      ]
    }
  ];

  const partnerBenefits = [
    {
      icon: DollarSign,
      title: 'Revenue Sharing',
      description: 'Earn competitive commissions on every referral that converts to a booking'
    },
    {
      icon: Sparkles,
      title: 'White-Glove Support',
      description: 'Dedicated partnership manager to ensure smooth operations and client satisfaction'
    },
    {
      icon: FileText,
      title: 'Marketing Support',
      description: 'Co-branded collateral, digital assets, and promotional materials at no cost'
    },
    {
      icon: Users,
      title: 'Client Experience',
      description: 'Your clients receive the same premium ACE experience with your brand visibility'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={getImageUrl(content('hero.background_image'), 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBhcnRuZXJzaGlwfGVufDB8fHx8MTc2MTYxMzAxNnww&ixlib=rb-4.1.0&q=85')}
            alt="Partnership"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-6 flex items-center">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Handshake className="h-6 w-6 text-red-600" />
              <span className="text-red-600 font-semibold">PARTNERSHIP PROGRAM</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {content('hero.title', 'Grow Together With ACE').split(' ').slice(0, 2).join(' ')}<br />{content('hero.title', 'Grow Together With ACE').split(' ').slice(2).join(' ')}
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              {content('hero.subtitle', 'Join our network of premium partners and add value to your clients with luxury automotive services')}
            </p>
            <Button 
              onClick={() => document.getElementById('partner-form').scrollIntoView({ behavior: 'smooth' })}
              className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold"
            >
              {content('hero.cta_primary', 'Become a Partner')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Partner Benefits */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-black mb-4">Why Partner With ACE?</h2>
          <p className="text-xl text-gray-600">Enhance your service offerings while generating additional revenue</p>
        </div>

        <div className="grid md:grid-cols-4 gap-8">
          {partnerBenefits.map((benefit, idx) => {
            const Icon = benefit.icon;
            return (
              <Card key={idx} className="p-6 text-center border-gray-200 hover:shadow-lg transition-all">
                <div className="h-14 w-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
                  <Icon className="h-7 w-7 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Partner Types */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-black text-center mb-16">Partnership Opportunities</h2>

          <div className="grid md:grid-cols-2 gap-8">
            {partnerTypes.map((type, idx) => {
              const Icon = type.icon;
              return (
                <Card key={idx} className="p-8 border-gray-200 hover:shadow-xl transition-all">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="h-14 w-14 rounded-full bg-red-600 flex items-center justify-center">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-black">{type.title}</h3>
                      <p className="text-gray-600">{type.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2 mt-6">
                    {type.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Wedding Planner Spotlight */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-red-600 font-semibold text-sm tracking-wider">FEATURED PROGRAM</span>
            <h2 className="text-4xl font-bold text-black mt-2 mb-6">Wedding Planner Partnership</h2>
            <p className="text-xl text-gray-600 mb-6">
              Our most popular partnership program. Wedding planners and coordinators trust ACE to deliver unforgettable send-off experiences for their couples.
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-red-600 mr-3 mt-0.5" />
                <div>
                  <h4 className="font-bold text-black">10-15% Referral Commission</h4>
                  <p className="text-gray-600 text-sm">Earn on every successful booking you refer</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-red-600 mr-3 mt-0.5" />
                <div>
                  <h4 className="font-bold text-black">Preferred Vendor Status</h4>
                  <p className="text-gray-600 text-sm">Priority booking and dedicated support line</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-red-600 mr-3 mt-0.5" />
                <div>
                  <h4 className="font-bold text-black">Staging Coordination</h4>
                  <p className="text-gray-600 text-sm">We work with your timeline for perfect photo moments</p>
                </div>
              </div>
            </div>

            <Button 
              onClick={() => document.getElementById('partner-form').scrollIntoView({ behavior: 'smooth' })}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Apply for Wedding Partner Program
            </Button>
          </div>

          <div className="relative h-96 rounded-lg overflow-hidden">
            <img
              src="https://images.pexels.com/photos/3764984/pexels-photo-3764984.jpeg"
              alt="Wedding Transportation"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Partner Form */}
      <div id="partner-form" className="bg-gray-50 py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Apply to Partner</h2>
            <p className="text-xl text-gray-600">Tell us about your business and how we can work together</p>
          </div>

          <Card className="p-8 md:p-12 border-gray-200 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="businessName" className="text-black mb-2 block">Business Name *</Label>
                  <Input
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    required
                    className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                    data-testid="partner-business-name"
                  />
                </div>
                <div>
                  <Label htmlFor="contactName" className="text-black mb-2 block">Contact Name *</Label>
                  <Input
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                    data-testid="partner-contact-name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-black mb-2 block">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                    data-testid="partner-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-black mb-2 block">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                    data-testid="partner-phone"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="partnerType" className="text-black mb-2 block">Partnership Type *</Label>
                <Select 
                  value={formData.partnerType} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, partnerType: value }))}
                >
                  <SelectTrigger data-testid="partner-type-select">
                    <SelectValue placeholder="Select partnership type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wedding-planner">Wedding Planner/Coordinator</SelectItem>
                    <SelectItem value="venue">Wedding/Event Venue</SelectItem>
                    <SelectItem value="hotel">Hotel/Hospitality</SelectItem>
                    <SelectItem value="dealership">Auto Dealership</SelectItem>
                    <SelectItem value="event-planner">Corporate Event Planner</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="website" className="text-black mb-2 block">Website (Optional)</Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://"
                  className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                  data-testid="partner-website"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-black mb-2 block">Tell us about your business and partnership goals *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                  placeholder="How many clients do you serve annually? What services would you like to offer through ACE?"
                  data-testid="partner-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white text-lg"
                data-testid="partner-submit-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Partnership Application'}
              </Button>
            </form>
          </Card>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">Questions About Partnerships?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Our partnerships team is happy to discuss how we can create value together
          </p>
          <Button 
            onClick={() => navigate('/contact')}
            size="lg"
            className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white text-lg"
          >
            Contact Partnerships Team
          </Button>
        </div>
      </div>
      <CMSCustomSections pageId="partners" dark />
    </div>
  );
};

export default Partners;
